import { useState, useEffect } from "react";
import { createClient } from "@supabase/supabase-js";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Button, Table, Form, Row, Col } from "react-bootstrap";
import { es } from "date-fns/locale";
import { format } from "date-fns";

const { REACT_APP_SUPABASE_URL, REACT_APP_SUPABASE_KEY } = process.env;
const supabase = createClient(REACT_APP_SUPABASE_URL, REACT_APP_SUPABASE_KEY);

const AdminFechasLaborables = () => {
    // Estados para Horario Base
    const [horarioSemanal, setHorarioSemanal] = useState([]);
    
    // Estados para Excepciones
    const [fechasExcepcion, setFechasExcepcion] = useState([]);
    const [nuevaFecha, setNuevaFecha] = useState(new Date());
    const [nuevaAbierto, setNuevaAbierto] = useState(false);
    const [nuevaHoraInicio, setNuevaHoraInicio] = useState("10:00");
    const [nuevaHoraFin, setNuevaHoraFin] = useState("20:00");
    
    const [loading, setLoading] = useState(false);

    // Cargar todos los datos
    const fetchData = async () => {
        setLoading(true);
        
        // 1. Cargar horario semanal ordenado por día (Lunes a Domingo)
        const { data: dataSemana, error: errorSemana } = await supabase
            .from("horario_semanal")
            .select("*")
            .order("dia_semana", { ascending: true });
            
        // 2. Cargar excepciones por fecha
        const { data: dataExcepciones, error: errorExcepciones } = await supabase
            .from("dias_excepcion")
            .select("*")
            .order("fecha", { ascending: true });

        if (errorSemana || errorExcepciones) {
            alert("Error al cargar los datos de configuración.");
        } else {
            // Reordenar para que el Domingo (0) quede al final si se prefiere visualmente
            const lunesADomingo = [...dataSemana.filter(d => d.dia_semana !== 0), dataSemana.find(d => d.dia_semana === 0)].filter(Boolean);
            setHorarioSemanal(lunesADomingo);
            setFechasExcepcion(dataExcepciones);
        }
        setLoading(false);
    };

    useEffect(() => { fetchData(); }, []);

    // --- ACCIONES HORARIO SEMANAL ---
    const handleCambioSemanal = (index, campo, valor) => {
        const copia = [...horarioSemanal];
        copia[index][campo] = valor;
        setHorarioSemanal(copia);
    };

    const guardarDiaSemanal = async (dia) => {
        const { error } = await supabase
            .from("horario_semanal")
            .update({
                abierto: dia.abierto,
                hora_inicio: dia.abierto ? dia.hora_inicio : null,
                hora_fin: dia.abierto ? dia.hora_fin : null
            })
            .eq("id", dia.id);

        if (error) alert("Error al guardar: " + error.message);
        else alert(`Horario de los ${dia.nombre_dia}s actualizado correctamente.`);
    };

    // --- ACCIONES EXCEPCIONES ---
    const agregarExcepcion = async () => {
        const fechaStr = nuevaFecha.toISOString().split("T")[0];
        
        if (fechasExcepcion.some(f => f.fecha === fechaStr)) {
            return alert("Ya existe una regla de excepción para esta fecha específica.");
        }

        const { error } = await supabase.from("dias_excepcion").insert([{
            fecha: fechaStr,
            abierto: nuevaAbierto,
            hora_inicio: nuevaAbierto ? nuevaHoraInicio : null,
            hora_fin: nuevaAbierto ? nuevaHoraFin : null
        }]);

        if (error) alert("Error al agregar excepción: " + error.message);
        else fetchData();
    };

    const eliminarExcepcion = async (id) => {
        if (!window.confirm("¿Seguro que deseas eliminar esta fecha excepcional?")) return;
        const { error } = await supabase.from("dias_excepcion").delete().eq("id", id);
        if (error) alert("Error al eliminar: " + error.message);
        else fetchData();
    };

    // Formateador rápido de hora (quita los segundos de Postgres: "10:00:00" -> "10:00")
    const formatHora = (hora) => hora ? hora.slice(0, 5) : "";

    return (
        <div style={{ marginTop: "20px", width: "100%", paddingBottom: "50px" }}>
            
            {/* SECCIÓN 1: HORARIO SEMANAL BASE */}
            <h3 style={{ fontSize: "2.2rem", marginTop: "2rem", marginBottom: "1rem" }}>Horario Semanal Base</h3>
            <p className="text-muted">Configura tus condiciones normales de apertura semanales.</p>
            
            {loading ? <p>Cargando configuraciones...</p> : (
                <Table bordered hover align="middle">
                    <thead>
                        <tr>
                            <th>Día</th>
                            <th>¿Abres?</th>
                            <th>Hora Inicio</th>
                            <th>Hora Fin</th>
                            <th>Acción</th>
                        </tr>
                    </thead>
                    <tbody>
                        {horarioSemanal.map((dia, index) => (
                            <tr key={dia.id}>
                                <td><strong>{dia.nombre_dia}</strong></td>
                                <td>
                                    <Form.Check type="checkbox">
                                        <Form.Check.Input 
                                            type="checkbox" 
                                            checked={dia.abierto} 
                                            onChange={(e) => handleCambioSemanal(index, "abierto", e.target.checked)}
                                            style={{ width: "22px", height: "22px", cursor: "pointer", appearance: "none", backgroundColor: dia.abierto ? "#000" : "#ccc" }} 
                                        />
                                    </Form.Check>
                                </td>
                                <td>
                                    <Form.Control 
                                        type="time" 
                                        disabled={!dia.abierto}
                                        value={formatHora(dia.hora_inicio) || "10:00"}
                                        onChange={(e) => handleCambioSemanal(index, "hora_inicio", e.target.value)}
                                    />
                                </td>
                                <td>
                                    <Form.Control 
                                        type="time" 
                                        disabled={!dia.abierto}
                                        value={formatHora(dia.hora_fin) || "20:00"}
                                        onChange={(e) => handleCambioSemanal(index, "hora_fin", e.target.value)}
                                    />
                                </td>
                                <td>
                                    <Button variant="dark" size="sm" onClick={() => guardarDiaSemanal(dia)}>Guardar</Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            <hr style={{ margin: "3rem 0" }} />

            {/* SECCIÓN 2: GESTIÓN DE EXCEPCIONES POR FECHA */}
            <h3 style={{ fontSize: "2.2rem" }}>Excepciones Puntuales (Fechas del Calendario)</h3>
            <p className="text-muted">Añade días específicos que sobreescriben el comportamiento de arriba (festivos, aperturas extra, etc.).</p>
            
            <div style={{ backgroundColor: "#f8f9fa", padding: "20px", borderRadius: "8px", marginBottom: "20px" }}>
                <h5>Agregar una Fecha Excepcional</h5>
                <Row className="align-items-center g-3">
                    <Col xs="auto">
                        <label className="d-block text-muted small">Selecciona Día</label>
                        <DatePicker locale={es} selected={nuevaFecha} onChange={setNuevaFecha} dateFormat="yyyy-MM-dd" className="form-control" />
                    </Col>
                    <Col xs="auto" className="pt-3">
                        <Form.Check type="checkbox" label="¿Se abre este día?">
                            <Form.Check.Input 
                                type="checkbox" 
                                checked={nuevaAbierto} 
                                onChange={(e) => setNuevaAbierto(e.target.checked)}
                                style={{ width: "20px", height: "20px", cursor: "pointer", appearance: "none", backgroundColor: nuevaAbierto ? "#000" : "#ccc", display: "inline-block" }} 
                            />
                        </Form.Check>
                    </Col>
                    <Col xs="auto">
                        <label className="d-block text-muted small">Hora Inicio</label>
                        <Form.Control type="time" disabled={!nuevaAbierto} value={nuevaHoraInicio} onChange={(e) => setNuevaHoraInicio(e.target.value)} />
                    </Col>
                    <Col xs="auto">
                        <label className="d-block text-muted small">Hora Fin</label>
                        <Form.Control type="time" disabled={!nuevaAbierto} value={nuevaHoraFin} onChange={(e) => setNuevaHoraFin(e.target.value)} />
                    </Col>
                    <Col xs="auto" className="pt-3">
                        <Button variant="primary" onClick={agregarExcepcion}>Añadir Excepción</Button>
                    </Col>
                </Row>
            </div>

            {/* TABLA DE EXCEPCIONES ACTUALES */}
            <h5 style={{ marginTop: "1.5rem" }}>Listado de Excepciones Guardadas</h5>
            {fechasExcepcion.length === 0 ? <p className="text-muted">No hay ninguna fecha excepcional configurada en el sistema.</p> : (
                <Table bordered hover>
                    <thead>
                        <tr>
                            <th>Fecha</th>
                            <th>Estado</th>
                            <th>Horario Planificado</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {fechasExcepcion.map((ex) => (
                            <tr key={ex.id}>
                                <td>{format(new Date(ex.fecha), "dd/MM/yyyy", { locale: es })}</td>
                                <td>
                                    <span className={`badge ${ex.abierto ? 'bg-success' : 'bg-danger'}`}>
                                        {ex.abierto ? "ABIERTO EXCEPCIONAL" : "CERRADO POR EXCEPCIÓN"}
                                    </span>
                                </td>
                                <td>{ex.abierto ? `${formatHora(ex.hora_inicio)} a ${formatHora(ex.hora_fin)}` : "Sin servicio"}</td>
                                <td>
                                    <Button variant="danger" size="sm" onClick={() => eliminarExcepcion(ex.id)}>Eliminar</Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
        </div>
    );
};

export default AdminFechasLaborables;